import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gurukulService, donationService, viewCounterService, adminService } from '../../services';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { API_BASE_URL } from '../../constants/config';
import PartyDonationModal from '../../components/common/PartyDonationModal';

const Home = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [gurukuls, setGurukuls] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState({});
  const [showPartyDonateModal, setShowPartyDonateModal] = useState(false);
  const [bannerUrl, setBannerUrl] = useState('https://republicaimg.nagariknewscdn.com/shared/web/uploads/media/sanskrit-gurukul-student.jpg');
  const viewCounted = useRef(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (viewCounted.current) return;
    viewCounted.current = true;
    viewCounterService.incrementHomepage().catch(() => { });
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [candidatesData, statsData, bannerData] = await Promise.all([
        gurukulService.getAllGurukuls({ limit: 20, offset: 0, sortBy: 'id', order: 'DESC' }),
        donationService.getPublicStats(),
        adminService.getBannerSettings().catch(() => null)
      ]);

      const bannerUrlData = bannerData?.data?.data?.bannerImageUrl || bannerData?.data?.bannerImageUrl;

      if (bannerUrlData) {
        let url = bannerUrlData;

        // Handle base64 data URLs separately
        if (url && url.startsWith('data:')) {
          setBannerUrl(url);
        } else {
          // Handle relative URLs
          if (url && !url.startsWith('http')) {
            const baseUrl = API_BASE_URL.split('/main/api/v1')[0];
            url = `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
          }
          // Add cache buster only for non-base64 URLs
          setBannerUrl(`${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`);
        }
      }

      // Handle API response structure: { status, statusCode, data: { items: [], totalCount } }
      const candidatesList = candidatesData?.data?.items || [];

      // Fisher-Yates Shuffle
      for (let i = candidatesList.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [candidatesList[i], candidatesList[j]] = [candidatesList[j], candidatesList[i]];
      }

      const stats = {
        totalRaised: statsData?.data?.totalAmountRaised || 0,
        totalDonors: statsData?.data?.totalDonors || 0,
        totalCandidates: statsData?.data?.totalCandidates || candidatesList.length,
      };

      setGurukuls(candidatesList);
      setStats(stats);
    } catch (error) {
      console.error('Error fetching data:', error);
      setGurukuls([]); // Set to empty array on error
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50/30">
      {/* Hero Section */}
      <div className="relative text-white overflow-hidden">
        {/* Hero background image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-700"
          style={{ backgroundImage: `url('${bannerUrl}')` }}
        ></div>
        {/* Dark gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-primary-900/70"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 md:py-48">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
              {t('home.heroTitle')}
            </h1>
            <p className="text-xl md:text-2xl mb-10 text-primary-50 max-w-3xl mx-auto">
              {t('home.heroSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setShowPartyDonateModal(true)}
                className="inline-flex items-center justify-center bg-white text-primary-700 px-8 py-4 rounded-button font-semibold hover:bg-primary-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <span className="w-6 h-6 mr-2.5 flex items-center justify-center bg-primary-100 text-primary-700 rounded-full text-xs font-bold border border-primary-200 shadow-sm flex-shrink-0">
                  रू
                </span>
                {t('nav.donateToGurukulFund')}
              </button>
              <Link
                to="/gurukuls"
                className="inline-flex items-center justify-center bg-primary-500 bg-opacity-20 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-button font-semibold hover:bg-opacity-30 transition-all duration-200"
              >
                {t('home.viewGurukul')}
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
              {/* Check KYC Status Button - Commented Out  */}
              {/* <Link
                to="/check-kyc-status"
                className="inline-flex items-center justify-center bg-orange-500 bg-opacity-10 backdrop-blur-sm border-2 border-orange-400 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-600 hover:bg-opacity-20 transition-all duration-200"
              >
                {t('home.checkKycStatus')}
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </Link> */}

            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      {stats && (
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6 rounded-xl bg-gradient-to-br from-primary-50 to-primary-100 hover:shadow-lg transition-shadow duration-200">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-primary-100 rounded-2xl mb-4 text-primary-600 shadow-sm border border-primary-200">
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C10.8954 2 10 2.89543 10 4C10 5.10457 10.8954 6 12 6C13.1046 6 14 5.10457 14 4C14 2.89543 13.1046 2 12 2Z" fill="currentColor" fillOpacity="0.2" />
                    <path d="M12 2V6M9 4L15 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M7 10C7 8.89543 7.89543 8 9 8H15C16.1046 8 17 8.89543 17 10V12C17 14.7614 14.7614 17 12 17C9.23858 17 7 14.7614 7 12V10Z" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M8 11.5C8 11.5 9 10 12 10C15 10 16 11.5 16 11.5" stroke="currentColor" strokeWidth="1" strokeDasharray="1 2" />
                    <path d="M8 18H16M9 21H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
                <div className="text-4xl font-bold text-primary-700 mb-2">
                  {formatCurrency(stats.totalRaised || 0, lang)}
                </div>
                <div className="text-gray-600 font-medium">{t('home.totalRaised')}</div>
              </div>
              <div className="text-center p-6 rounded-xl bg-gradient-to-br from-green-50 to-green-100 hover:shadow-lg transition-shadow duration-200">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-2xl mb-4 text-green-600 shadow-sm border border-green-200">
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 20V7M12 7C12 7 10 3 7 3C4 3 4 5 4 5L8 13C8 13 9 15 12 15M12 7C12 7 14 3 17 3C20 3 20 5 20 5L16 13C16 13 15 15 12 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M10 18H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
                <div className="text-4xl font-bold text-green-700 mb-2">
                  {formatNumber(stats.totalDonors || 0, lang)}
                </div>
                <div className="text-gray-600 font-medium">{t('home.totalDonors')}</div>
              </div>
              <div className="text-center p-6 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 hover:shadow-lg transition-shadow duration-200">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-purple-100 rounded-2xl mb-4 text-purple-600 shadow-sm border border-purple-200">
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L6 5V7H18V5L12 2Z" fill="currentColor" fillOpacity="0.2" />
                    <path d="M12 2L6 5V7H18V5L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                    <path d="M4 10H20M5 10V20H19V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9 10V20M15 10V20M12 10V20" stroke="currentColor" strokeWidth="1" strokeOpacity="0.5" />
                    <path d="M7 20H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
                <div className="text-4xl font-bold text-purple-700 mb-2">
                  {formatNumber(stats.totalCandidates || (Array.isArray(gurukuls) ? gurukuls.length : 0), lang)}
                </div>
                <div className="text-gray-600 font-medium">{t('home.totalGurukul')}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Featured Gurukuls */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('home.featuredGurukul')}</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('home.heroSubtitle')}
          </p>
        </div>
        {Array.isArray(gurukuls) && gurukuls.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {gurukuls.map((gurukul) => (
                <div key={gurukul.id} className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-gray-100">
                  <Link
                    to={`/gurukuls/${gurukul.id}`}
                    className="relative h-72 bg-gradient-to-br from-primary-50 to-primary-100 overflow-hidden block"
                    aria-label={`${t('actions.viewProfile')} - ${gurukul.name}`}
                  >
                    {gurukul.photoKeyUrl && !imageErrors[gurukul.id] ? (
                      <img
                        src={gurukul.photoKeyUrl}
                        alt={gurukul.name}
                        className="absolute inset-0 w-full h-full object-cover scale-110 group-hover:scale-105 transition-transform duration-500"
                        style={{ objectPosition: 'center 20%' }}
                        onError={() => {
                          setImageErrors(prev => ({ ...prev, [gurukul.id]: true }));
                        }}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200">
                        <div className="w-24 h-24 rounded-full bg-white shadow-lg flex items-center justify-center">
                          <span className="text-5xl font-bold text-primary-600">
                            {gurukul.name.charAt(0)}
                          </span>
                        </div>
                      </div>
                    )}
                    {/* Subtle gradient overlay at the bottom for better text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    {/* Official Badge */}
                    {/* <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1 shadow-lg flex items-center gap-1.5 border border-primary-200">
                      <svg className="w-3.5 h-3.5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-xs font-bold text-primary-700">Official</span>
                    </div> */}
                  </Link>
                  <div className="p-6">
                    <Link to={`/gurukuls/${gurukul.id}`} className="block">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                        {gurukul.name}
                      </h3>
                    </Link>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                      <svg className="w-4 h-4 text-primary-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="font-medium">{gurukul.nagarpalikaGaupalika}, {gurukul.district}</span>
                    </div>
                    <Link
                      to={`/gurukuls/${gurukul.id}`}
                      className="block w-full text-center bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      {t('actions.viewProfile')}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/gurukuls"
                className="inline-block bg-primary-600 text-white px-8 py-3 rounded-button font-semibold hover:bg-primary-700 transition"
              >
                {t('home.viewAllGurukul')}
              </Link>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">{t('home.noFeaturedGurukul')}</p>
          </div>
        )}
      </div>

      {/* Party Donation Modal */}
      <PartyDonationModal
        isOpen={showPartyDonateModal}
        onClose={() => setShowPartyDonateModal(false)}
      />
    </div>
  );
};

export default Home;
